import {ApplicantDemographics, matchApplicantDemographicsByType} from 'ApplicationEntry/models/ApplicantDemographics';
import {ApplicantType} from 'ApplicationEntry/models/ApplicantType';
import * as G from 'ApplicationEntry/models/GroupApplicantDemographics';
import * as NY from 'ApplicationEntry/models/NewYorkApplicantDemographics';
import * as O from 'ApplicationEntry/models/OrdinaryApplicantDemographics';
import {Sex} from 'ApplicationEntry/models/Sex';
import {createAction} from 'Shared/utilities/actionCreator';
import {reverseEnum} from 'Shared/utilities/enumUtility';

import {Async} from 'Core/models/Async';
import {ZipCode} from 'ApplicationEntry/models/ZipCode';

export enum ApplicantDemographicsActionType {
  CHANGE_NAME = 'applicantDemographics/CHANGE_NAME',
  CHANGE_TYPE = 'applicantDemographics/CHANGE_TYPE',
  CHANGE_SEX = 'applicantDemographics/CHANGE_SEX',
  CHANGE_DATE_OF_BIRTH = 'applicantDemographics/CHANGE_DATE_OF_BIRTH',
  CHANGE_ADDRESS = 'applicantDemographics/CHANGE_ADDRESS',
  CHANGE_ADDRESS_LINE2 = 'applicantDemographics/CHANGE_ADDRESS_LINE2',
  CHANGE_CITY = 'applicantDemographics/CHANGE_CITY',
  CHANGE_STATE = 'applicantDemographics/CHANGE_STATE',
  CHANGE_ZIP = 'applicantDemographics/CHANGE_ZIP',
  CHANGE_SOCIAL_SECURITY_NUMBER = 'applicantDemographics/CHANGE_SOCIAL_SECURITY_NUMBER',
  CHANGE_HIRE_DATE = 'applicantDemographics/CHANGE_HIRE_DATE',
  CHANGE_EMAIL = 'applicantDemographics/CHANGE_EMAIL',
  CHANGE_HEIGHT = 'applicantDemographics/CHANGE_HEIGHT',
  CHANGE_WEIGHT = 'applicantDemographics/CHANGE_WEIGHT',
  CHANGE_IS_FULLTIME_STUDENT = 'applicantDemographics/CHANGE_IS_FULLTIME_STUDENT',
  CHANGE_OCCUPATION = 'applicantDemographics/CHANGE_OCCUPATION',
  CHANGE_USER_OCCUPATION = 'applicantDemographics/CHANGE_USER_OCCUPATION',
  CHANGE_PHONE = 'applicantDemographics/CHANGE_PHONE',
  GET_OCCUPATION = 'applicantDemographics/GET_OCCUPATION',
  GET_OCCUPATION_MESSAGE = 'applicantDemographics/GET_OCCUPATION_MESSAGE',
}

export const isApplicantDemographicsAction = (action: any): action is ApplicantDemographicsAction =>
  action.type in reverseEnum(ApplicantDemographicsActionType);

export const changeName = (name: string) => createAction(ApplicantDemographicsActionType.CHANGE_NAME, {name});
export const changeType = (type: ApplicantType) => createAction(ApplicantDemographicsActionType.CHANGE_TYPE, {type});
export const changeSex = (sex: Sex) => createAction(ApplicantDemographicsActionType.CHANGE_SEX, {sex});
export const changePhone = (phone: string) => createAction(ApplicantDemographicsActionType.CHANGE_PHONE, {phone});
export const changeAddress = (address: string) =>
  createAction(ApplicantDemographicsActionType.CHANGE_ADDRESS, {address});
export const changeAddressLine2 = (addressLine2: string) =>
  createAction(ApplicantDemographicsActionType.CHANGE_ADDRESS_LINE2, {addressLine2});
export const changeCity = (city: string) => createAction(ApplicantDemographicsActionType.CHANGE_CITY, {city});
export const changeState = (state: string) => createAction(ApplicantDemographicsActionType.CHANGE_STATE, {state});
export const changeZip = (zip: ZipCode) => createAction(ApplicantDemographicsActionType.CHANGE_ZIP, {zip});
export const changeOccupation = (occupation: Async<string>) =>
  createAction(ApplicantDemographicsActionType.CHANGE_OCCUPATION, {occupation});
export const changeUserOccupation = (occupation: string, code: string, description: string, underwritingcode: string) =>
  createAction(ApplicantDemographicsActionType.CHANGE_USER_OCCUPATION, {
    occupation,
    code,
    description,
    underwritingcode,
  });
export const changeDateOfBirth = (dateOfBirth: Date | '') =>
  createAction(ApplicantDemographicsActionType.CHANGE_DATE_OF_BIRTH, {dateOfBirth});
export const changeSocialSecurityNumber = (socialSecurityNumber: string) =>
  createAction(ApplicantDemographicsActionType.CHANGE_SOCIAL_SECURITY_NUMBER, {socialSecurityNumber});
export const changeHireDate = (hireDate: Date | '') =>
  createAction(ApplicantDemographicsActionType.CHANGE_HIRE_DATE, {hireDate});
export const changeEmail = (email: string) => createAction(ApplicantDemographicsActionType.CHANGE_EMAIL, {email});
export const changeHeight = (feet: number | '', inches: number | '') =>
  createAction(ApplicantDemographicsActionType.CHANGE_HEIGHT, {feet, inches});
export const changeWeight = (weight: string) => createAction(ApplicantDemographicsActionType.CHANGE_WEIGHT, {weight});
export const changeIsFullTimeStudent = (isFullTimeStudent: boolean | '') =>
  createAction(ApplicantDemographicsActionType.CHANGE_IS_FULLTIME_STUDENT, {isFullTimeStudent});
export const getOccupation = (occupation: string) =>
  createAction(ApplicantDemographicsActionType.GET_OCCUPATION, {occupation});
export const changeOccupationError = (occupation: string) =>
  createAction(ApplicantDemographicsActionType.GET_OCCUPATION_MESSAGE, {occupation});

export type ApplicantDemographicsAction =
  | ReturnType<typeof changeName>
  | ReturnType<typeof changeType>
  | ReturnType<typeof changeSex>
  | ReturnType<typeof changePhone>
  | ReturnType<typeof changeAddress>
  | ReturnType<typeof changeAddressLine2>
  | ReturnType<typeof changeCity>
  | ReturnType<typeof changeState>
  | ReturnType<typeof changeZip>
  | ReturnType<typeof changeDateOfBirth>
  | ReturnType<typeof changeSocialSecurityNumber>
  | ReturnType<typeof changeHireDate>
  | ReturnType<typeof changeEmail>
  | ReturnType<typeof changeHeight>
  | ReturnType<typeof changeWeight>
  | ReturnType<typeof changeOccupation>
  | ReturnType<typeof changeUserOccupation>
  | ReturnType<typeof changeIsFullTimeStudent>
  | ReturnType<typeof changeOccupationError>;

type ApplicantDemographicsState = ApplicantDemographics;

export default (state: ApplicantDemographicsState, action: ApplicantDemographicsAction): ApplicantDemographicsState => {
  switch (action.type) {
    case ApplicantDemographicsActionType.CHANGE_NAME:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Group: G.changeName(action.payload.name),
        NewYork: NY.changeName(action.payload.name),
        Ordinary: O.changeName(action.payload.name),
      })(state);
    case ApplicantDemographicsActionType.CHANGE_TYPE:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeType(action.payload.type),
        _: () => state,
      })(state);
    case ApplicantDemographicsActionType.CHANGE_SEX:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Group: G.changeSex(action.payload.sex),
        NewYork: NY.changeSex(action.payload.sex),
        Ordinary: O.changeSex(action.payload.sex),
      })(state);
    case ApplicantDemographicsActionType.CHANGE_PHONE:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Group: G.changePhone(action.payload.phone),
        NewYork: NY.changePhone(action.payload.phone),
        Ordinary: O.changePhone(action.payload.phone),
      })(state);
    case ApplicantDemographicsActionType.CHANGE_ADDRESS:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Group: G.changeAddress(action.payload.address),
        NewYork: NY.changeAddress(action.payload.address),
        Ordinary: O.changeAddress(action.payload.address),
      })(state);
    case ApplicantDemographicsActionType.CHANGE_ADDRESS_LINE2:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Group: G.changeAddressLine2(action.payload.addressLine2),
        NewYork: NY.changeAddressLine2(action.payload.addressLine2),
        Ordinary: O.changeAddressLine2(action.payload.addressLine2),
      })(state);
    case ApplicantDemographicsActionType.CHANGE_CITY:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeCity(action.payload.city),
        Group: G.changeCity(action.payload.city),
        NewYork: NY.changeCity(action.payload.city),
      })(state);
    case ApplicantDemographicsActionType.CHANGE_STATE:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeState(action.payload.state),
        Group: G.changeState(action.payload.state),
        NewYork: NY.changeState(action.payload.state),
      })(state);
    case ApplicantDemographicsActionType.CHANGE_ZIP:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeZip(action.payload.zip),
        Group: G.changeZip(action.payload.zip),
        NewYork: NY.changeZip(action.payload.zip),
      })(state);
    case ApplicantDemographicsActionType.CHANGE_DATE_OF_BIRTH:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Group: G.changeDateOfBirth(action.payload.dateOfBirth),
        NewYork: NY.changeDateOfBirth(action.payload.dateOfBirth),
        Ordinary: O.changeDateOfBirth(action.payload.dateOfBirth),
      })(state);
    case ApplicantDemographicsActionType.CHANGE_SOCIAL_SECURITY_NUMBER:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Group: G.changeSocialSecurityNumber(action.payload.socialSecurityNumber),
        NewYork: NY.changeSocialSecurityNumber(action.payload.socialSecurityNumber),
        Ordinary: O.changeSocialSecurityNumber(action.payload.socialSecurityNumber),
      })(state);
    case ApplicantDemographicsActionType.CHANGE_HIRE_DATE:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeHireDate(action.payload.hireDate),
        Group: G.changeHireDate(action.payload.hireDate),
        _: () => state,
      })(state);
    case ApplicantDemographicsActionType.CHANGE_EMAIL:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeEmail(action.payload.email),
        Group: G.changeEmail(action.payload.email),
        _: () => state,
      })(state);
    case ApplicantDemographicsActionType.CHANGE_HEIGHT:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeHeight(action.payload.feet, action.payload.inches),
        _: () => state,
      })(state);
    case ApplicantDemographicsActionType.CHANGE_WEIGHT:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeWeight(action.payload.weight),
        _: () => state,
      })(state);
    case ApplicantDemographicsActionType.CHANGE_IS_FULLTIME_STUDENT:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeIsFullTimeStudent(action.payload.isFullTimeStudent),
        _: () => state,
      })(state);
    case ApplicantDemographicsActionType.CHANGE_OCCUPATION:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeOccupation(action.payload.occupation),
        NewYork: NY.changeOccupation(action.payload.occupation),
        _: () => state,
      })(state);
    case ApplicantDemographicsActionType.CHANGE_USER_OCCUPATION:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeUserOccupation(
          action.payload.occupation,
          action.payload.code,
          action.payload.description,
          action.payload.underwritingcode
        ),
        NewYork: NY.changeUserOccupation(
          action.payload.occupation,
          action.payload.code,
          action.payload.description,
          action.payload.underwritingcode
        ),
        _: () => state,
      })(state);
    case ApplicantDemographicsActionType.GET_OCCUPATION_MESSAGE:
      return matchApplicantDemographicsByType<ApplicantDemographics>({
        Ordinary: O.changeOccupationError(action.payload.occupation),
        NewYork: NY.changeOccupationError(action.payload.occupation),
        Group: G.changeOccupationError(action.payload.occupation),
      })(state);
    default:
      return state;
  }
};
